import { useCallback, useEffect, useState } from "react";

/**
 * useTranslation - drives the Google Translate widget via the `googtrans`
 * cookie. Setting `googtrans=/en/<target>` and reloading the page (or
 * dispatching the widget's internal change event) translates the DOM.
 *
 * Persists the user's choice in localStorage. Reads on every mount so
 * the chosen language survives page navigations.
 */

const STORAGE_KEY = "vilora-lang";
const COOKIE_NAME = "googtrans";

const setCookie = (value, days = 365) => {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  // Set on both root host and current host, on both `.` prefix and bare,
  // because Google Translate looks for the cookie under varying scopes.
  const host = window.location.hostname;
  const apex = host.split(".").slice(-2).join(".");
  const expires = `expires=${d.toUTCString()}`;
  document.cookie = `${COOKIE_NAME}=${value};${expires};path=/`;
  if (host !== "localhost") {
    document.cookie = `${COOKIE_NAME}=${value};${expires};path=/;domain=${host}`;
    if (apex && apex !== host) {
      document.cookie = `${COOKIE_NAME}=${value};${expires};path=/;domain=.${apex}`;
    }
  }
};

const clearCookie = () => {
  const host = window.location.hostname;
  const apex = host.split(".").slice(-2).join(".");
  const past = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `${COOKIE_NAME}=;${past};path=/`;
  if (host !== "localhost") {
    document.cookie = `${COOKIE_NAME}=;${past};path=/;domain=${host}`;
    if (apex && apex !== host) {
      document.cookie = `${COOKIE_NAME}=;${past};path=/;domain=.${apex}`;
    }
  }
};

const readSavedLanguage = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || "en";
  } catch {
    return "en";
  }
};

export function useTranslation() {
  const [current, setCurrent] = useState(readSavedLanguage);

  // On initial mount, if the user previously chose a non-English language,
  // re-apply the cookie so the page translates again on this load.
  useEffect(() => {
    if (current && current !== "en") {
      setCookie(`/en/${current}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeLanguage = useCallback((code) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* no-op - storage may be unavailable */
    }
    setCurrent(code);

    if (!code || code === "en") {
      clearCookie();
    } else {
      setCookie(`/en/${code}`);
    }

    // Hard-reload so Google Translate re-applies on the next render. This
    // is the most reliable path - programmatic .select.value on the
    // injected select fires inconsistently across Google's iframe states.
    window.location.reload();
  }, []);

  return { current, changeLanguage };
}
